import React from 'react';
import {
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip,
} from 'recharts';
import { Card } from '@/components/ui/card';
import { BarChart3 } from 'lucide-react';

// Founder UAT 2026-06 — cross-suite executive visual analytics standard.
// Color semantics (Founder decision): RED risk/failed, BLUE informational,
// GREEN completed/healthy, YELLOW pending, ORANGE in progress/warning.
// NO line charts anywhere. Real canonical data only — never fabricate.

export const SEM = {
  red: '#EF4444',
  blue: '#3B82F6',
  green: '#10B981',
  yellow: '#EAB308',
  orange: '#F97316',
  teal: '#0D9488',
  violet: '#8B5CF6',
  slate: '#94A3B8',
};

export function ChartEmpty({ text, testid }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 px-4 text-center" data-testid={testid}>
      <BarChart3 className="h-7 w-7 text-muted-foreground/40 mb-2" />
      <p className="text-sm text-muted-foreground max-w-sm">{text}</p>
    </div>
  );
}

// data: [{name, value, color}] — value>0 rows only should be passed.
export function DonutCard({ title, subtitle, data, emptyText, testid, accent = SEM.teal }) {
  const rows = (data || []).filter((d) => d.value > 0);
  const total = rows.reduce((s, d) => s + d.value, 0);
  return (
    <Card className="p-5 shadow-card relative overflow-hidden" data-testid={testid}>
      <span className="absolute inset-x-0 top-0 h-1" style={{ backgroundColor: accent }} aria-hidden="true" />
      <h3 className="font-semibold text-base mb-0.5">{title}</h3>
      {subtitle && <p className="text-xs text-muted-foreground mb-1">{subtitle}</p>}
      {total === 0 ? (
        <ChartEmpty text={emptyText} testid={testid ? `${testid}-empty` : undefined} />
      ) : (
        <div className="h-48 flex items-center" style={{ minHeight: 192 }}>
          <ResponsiveContainer width="52%" height="100%" minHeight={180}>
            <PieChart>
              <Pie data={rows} dataKey="value" nameKey="name" innerRadius={38}
                   outerRadius={68} paddingAngle={3} stroke="none">
                {rows.map((d) => <Cell key={d.name} fill={d.color || SEM.blue} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <ul className="flex-1 space-y-1.5 pr-1 overflow-auto max-h-44">
            {rows.map((d) => (
              <li key={d.name} className="flex items-center gap-1.5 text-xs">
                <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ backgroundColor: d.color || SEM.blue }} />
                <span className="flex-1 truncate" title={d.name}>{d.name}</span>
                <span className="font-semibold tabular-nums">{d.value}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

// data: [{name, value, color}] — horizontal bars.
export function BarsCard({ title, subtitle, data, emptyText, testid, accent = SEM.blue, unit }) {
  const rows = (data || []).filter((d) => d.value > 0);
  return (
    <Card className="p-5 shadow-card relative overflow-hidden" data-testid={testid}>
      <span className="absolute inset-x-0 top-0 h-1" style={{ backgroundColor: accent }} aria-hidden="true" />
      <h3 className="font-semibold text-base mb-0.5">{title}</h3>
      {subtitle && <p className="text-xs text-muted-foreground mb-1">{subtitle}</p>}
      {rows.length === 0 ? (
        <ChartEmpty text={emptyText} testid={testid ? `${testid}-empty` : undefined} />
      ) : (
        <div className="h-48" style={{ minHeight: 192 }}>
          <ResponsiveContainer minHeight={180}>
            <BarChart data={rows} layout="vertical" margin={{ left: 4, right: 26, top: 4, bottom: 0 }}>
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }}
                     domain={unit === '%' ? [0, 100] : undefined} />
              <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => (unit === '%' ? `${v}%` : v)} />
              <Bar dataKey="value" radius={[0, 5, 5, 0]} barSize={16}>
                {rows.map((d) => <Cell key={d.name} fill={d.color || SEM.blue} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

// rows: [{label, value, color}] — status pipeline with progress bars.
export function ProgressCard({ title, subtitle, rows, total, emptyText, testid, accent = SEM.orange }) {
  const tot = total ?? (rows || []).reduce((s, r) => s + r.value, 0);
  return (
    <Card className="p-5 shadow-card relative overflow-hidden" data-testid={testid}>
      <span className="absolute inset-x-0 top-0 h-1" style={{ backgroundColor: accent }} aria-hidden="true" />
      <h3 className="font-semibold text-base mb-0.5">{title}</h3>
      {subtitle && <p className="text-xs text-muted-foreground mb-2">{subtitle}</p>}
      {tot === 0 ? (
        <ChartEmpty text={emptyText} testid={testid ? `${testid}-empty` : undefined} />
      ) : (
        <div className="space-y-2.5">
          {(rows || []).map((r) => (
            <div key={r.label}>
              <div className="flex justify-between text-xs mb-0.5">
                <span>{r.label}</span>
                <span className="font-semibold tabular-nums">{r.value}</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{
                  width: `${tot ? Math.round((r.value / tot) * 100) : 0}%`,
                  backgroundColor: r.color || SEM.blue,
                }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
